import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Grid, Tabs, TabPanel } from '_template';
import { CreateEmailTemplateMutation, UpdateEmailTemplateMutation } from 'modules/EmailTemplate/query';
import { EmailTemplateData } from 'modules/EmailTemplate/interfaces';
import { handleSaveData } from 'helpers/data/handleSaveData';
import TextField from 'components/TextField';
import { ROUTE } from 'constants/route';
import BodyHelp from './BodyHelp';

interface EmailTemplateFormProps {
  recordId?: string;
  data?: EmailTemplateData;
}

const EmailTemplateForm = ({ recordId, data }: EmailTemplateFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isNew = recordId === 'new';

  const [createMutation] = useMutation(CreateEmailTemplateMutation);
  const [updateMutation] = useMutation(UpdateEmailTemplateMutation);

  const onCancel = () => navigate(ROUTE.EMAIL_TEMPLATE_LIST);

  const validationSchema = Yup.object({
    name: Yup.string().required(t<string>('EMAIL_TEMPLATE.NAME_REQUIRED')),
  });

  const onSubmit = (newData: EmailTemplateData) => {
    const variables = {
      id: recordId,
      data: {
        name: newData.name,
        article: newData.article,
        bodyHtml: newData.bodyHtml,
      },
    };

    handleSaveData(isNew ? createMutation : updateMutation, variables, onCancel);
  };

  return (
    <Formik
      initialValues={{
        ...data,
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit} onCancel={onCancel}>
          <Tabs panelsToCard>
            <TabPanel label={t('GENERAL.BASIC_DATA')}>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <TextField
                    name="name"
                    label={t('EMAIL_TEMPLATE.NAME')}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="article"
                    label={t('EMAIL_TEMPLATE.ARTICLE')}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <TextField
                    name="bodyHtml"
                    label={t('EMAIL_TEMPLATE.BODY_HTML')}
                    multiline
                    rows={30}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <BodyHelp />
                </Grid>
              </Grid>
            </TabPanel>
          </Tabs>
        </Form>
      )}
    </Formik>
  );
};

export default EmailTemplateForm;
